import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_media_enterprise-media-items';

/** Internal endpoint called by the public media backend when a file is created. Creates domain events for the Enterprise Public Media system. */
export function itemUploadCallback(
  payload: object
): RequestOptionsFactory<any> {
  function __itemUploadCallback({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.ItemUploadCallback',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/upload/callback',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __itemUploadCallback;
}

/** Generates an upload URL for uploading local files to the Enterprise Public Media system. After uploading the file to the returned URL, the system processes it asynchronously. The item becomes available once processing completes. */
export function generateFileUploadUrl(
  payload: object
): RequestOptionsFactory<any> {
  function __generateFileUploadUrl({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.GenerateFileUploadUrl',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/upload/generate-url',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __generateFileUploadUrl;
}

/**
 * Imports a file from a URL into the Enterprise Public Media system.
 *
 * To import a single file from your local machine, call [Generate File Upload Url](#generatefileuploadurl).
 */
export function importFile(payload: object): RequestOptionsFactory<any> {
  function __importFile({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.ImportFile',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/upload/import',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.createdDate' },
              { path: 'item.updatedDate' },
              { path: 'item.assets.image.urlExpirationDate' },
              { path: 'item.assets.video.urlExpirationDate' },
              { path: 'item.assets.video.resolutions.urlExpirationDate' },
              {
                path: 'item.assets.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'item.assets.video.posters.urlExpirationDate' },
              { path: 'item.assets.vector.urlExpirationDate' },
              { path: 'item.assets.audio.urlExpirationDate' },
              { path: 'item.assets.document.urlExpirationDate' },
              { path: 'item.assets.archive.urlExpirationDate' },
              { path: 'item.assets.model3d.urlExpirationDate' },
              { path: 'item.assets.model3d.thumbnail.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __importFile;
}

/**
 * Imports multiple files from URLs in a single API call. Check the `itemMetadata` in each result to identify successes and failures, and use `bulkActionMetadata` for an overview of total succeeded and failed operations.
 *
 * To import a single file from a URL, call [Import File](#importfile).
 */
export function bulkImportFiles(payload: object): RequestOptionsFactory<any> {
  function __bulkImportFiles({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.BulkImportFiles',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/bulk/files/import',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.assets.image.urlExpirationDate' },
              { path: 'results.item.assets.video.urlExpirationDate' },
              {
                path: 'results.item.assets.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.assets.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'results.item.assets.video.posters.urlExpirationDate' },
              { path: 'results.item.assets.vector.urlExpirationDate' },
              { path: 'results.item.assets.audio.urlExpirationDate' },
              { path: 'results.item.assets.document.urlExpirationDate' },
              { path: 'results.item.assets.archive.urlExpirationDate' },
              { path: 'results.item.assets.model3d.urlExpirationDate' },
              {
                path: 'results.item.assets.model3d.thumbnail.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkImportFiles;
}

/**
 * Searches for items using text-based search criteria.
 *
 * Search Items runs with these defaults, which you can override:
 *
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * All filters support equality only. Each search must include a `categoryId` filter. Use this endpoint when you need to search for items by text. For retrieving items with custom sorting, use [Query Items](#queryitems).
 *
 * To learn about working with search methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 */
export function searchItems(payload: object): RequestOptionsFactory<any> {
  function __searchItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.SearchItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/search',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'items.createdDate' },
              { path: 'items.updatedDate' },
              { path: 'items.assets.image.urlExpirationDate' },
              { path: 'items.assets.video.urlExpirationDate' },
              { path: 'items.assets.video.resolutions.urlExpirationDate' },
              {
                path: 'items.assets.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'items.assets.video.posters.urlExpirationDate' },
              { path: 'items.assets.vector.urlExpirationDate' },
              { path: 'items.assets.audio.urlExpirationDate' },
              { path: 'items.assets.document.urlExpirationDate' },
              { path: 'items.assets.archive.urlExpirationDate' },
              { path: 'items.assets.model3d.urlExpirationDate' },
              { path: 'items.assets.model3d.thumbnail.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchItems;
}

/**
 * Retrieves a list of up to 200 items, given the provided paging, filtering, and sorting.
 *
 * Query Items runs with these defaults, which you can override:
 *
 * - Sorted by curation order
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * Each query must include a `categoryId` filter. For text-based search, use [Search Items](#searchitems).
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 */
export function queryItems(payload: object): RequestOptionsFactory<any> {
  function __queryItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.QueryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'items.createdDate' },
              { path: 'items.updatedDate' },
              { path: 'items.assets.image.urlExpirationDate' },
              { path: 'items.assets.video.urlExpirationDate' },
              { path: 'items.assets.video.resolutions.urlExpirationDate' },
              {
                path: 'items.assets.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'items.assets.video.posters.urlExpirationDate' },
              { path: 'items.assets.vector.urlExpirationDate' },
              { path: 'items.assets.audio.urlExpirationDate' },
              { path: 'items.assets.document.urlExpirationDate' },
              { path: 'items.assets.archive.urlExpirationDate' },
              { path: 'items.assets.model3d.urlExpirationDate' },
              { path: 'items.assets.model3d.thumbnail.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryItems;
}

/**
 * Updates an item.
 * To update multiple items in a single API call, call [Bulk Update Item](#bulkupdateitem).
 */
export function updateItem(payload: object): RequestOptionsFactory<any> {
  function __updateItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'item.createdDate' },
          { path: 'item.updatedDate' },
          { path: 'item.assets.image.urlExpirationDate' },
          { path: 'item.assets.video.urlExpirationDate' },
          { path: 'item.assets.video.resolutions.urlExpirationDate' },
          { path: 'item.assets.video.resolutions.poster.urlExpirationDate' },
          { path: 'item.assets.video.posters.urlExpirationDate' },
          { path: 'item.assets.vector.urlExpirationDate' },
          { path: 'item.assets.audio.urlExpirationDate' },
          { path: 'item.assets.document.urlExpirationDate' },
          { path: 'item.assets.archive.urlExpirationDate' },
          { path: 'item.assets.model3d.urlExpirationDate' },
          { path: 'item.assets.model3d.thumbnail.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'PATCH' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.UpdateItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/{item.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.createdDate' },
              { path: 'item.updatedDate' },
              { path: 'item.assets.image.urlExpirationDate' },
              { path: 'item.assets.video.urlExpirationDate' },
              { path: 'item.assets.video.resolutions.urlExpirationDate' },
              {
                path: 'item.assets.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'item.assets.video.posters.urlExpirationDate' },
              { path: 'item.assets.vector.urlExpirationDate' },
              { path: 'item.assets.audio.urlExpirationDate' },
              { path: 'item.assets.document.urlExpirationDate' },
              { path: 'item.assets.archive.urlExpirationDate' },
              { path: 'item.assets.model3d.urlExpirationDate' },
              { path: 'item.assets.model3d.thumbnail.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateItem;
}

/**
 * Updates multiple items in a single API call.
 *
 * To update a single item, call [Update Item](#updateitem).
 */
export function bulkUpdateItem(payload: object): RequestOptionsFactory<any> {
  function __bulkUpdateItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'updateItemRequests.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'updateItemRequests.item.createdDate' },
          { path: 'updateItemRequests.item.updatedDate' },
          { path: 'updateItemRequests.item.assets.image.urlExpirationDate' },
          { path: 'updateItemRequests.item.assets.video.urlExpirationDate' },
          {
            path: 'updateItemRequests.item.assets.video.resolutions.urlExpirationDate',
          },
          {
            path: 'updateItemRequests.item.assets.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'updateItemRequests.item.assets.video.posters.urlExpirationDate',
          },
          { path: 'updateItemRequests.item.assets.vector.urlExpirationDate' },
          { path: 'updateItemRequests.item.assets.audio.urlExpirationDate' },
          { path: 'updateItemRequests.item.assets.document.urlExpirationDate' },
          { path: 'updateItemRequests.item.assets.archive.urlExpirationDate' },
          { path: 'updateItemRequests.item.assets.model3d.urlExpirationDate' },
          {
            path: 'updateItemRequests.item.assets.model3d.thumbnail.urlExpirationDate',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'PATCH' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.BulkUpdateItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/bulk/enterprise/items/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.assets.image.urlExpirationDate' },
              { path: 'results.item.assets.video.urlExpirationDate' },
              {
                path: 'results.item.assets.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.assets.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'results.item.assets.video.posters.urlExpirationDate' },
              { path: 'results.item.assets.vector.urlExpirationDate' },
              { path: 'results.item.assets.audio.urlExpirationDate' },
              { path: 'results.item.assets.document.urlExpirationDate' },
              { path: 'results.item.assets.archive.urlExpirationDate' },
              { path: 'results.item.assets.model3d.urlExpirationDate' },
              {
                path: 'results.item.assets.model3d.thumbnail.urlExpirationDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateItem;
}

/** Retrieves an item by ID. */
export function getItem(payload: object): RequestOptionsFactory<any> {
  function __getItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'GET' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.GetItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/{itemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.createdDate' },
              { path: 'item.updatedDate' },
              { path: 'item.assets.image.urlExpirationDate' },
              { path: 'item.assets.video.urlExpirationDate' },
              { path: 'item.assets.video.resolutions.urlExpirationDate' },
              {
                path: 'item.assets.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'item.assets.video.posters.urlExpirationDate' },
              { path: 'item.assets.vector.urlExpirationDate' },
              { path: 'item.assets.audio.urlExpirationDate' },
              { path: 'item.assets.document.urlExpirationDate' },
              { path: 'item.assets.archive.urlExpirationDate' },
              { path: 'item.assets.model3d.urlExpirationDate' },
              { path: 'item.assets.model3d.thumbnail.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getItem;
}

/**
 * Links an item to multiple categories. Adds to existing category links without removing them.
 *
 * To replace all category links, call [Overwrite Item Categories](#overwriteitemcategories). To remove specific category links, call [Unlink Item From Categories](#unlinkitemfromcategories).
 */
export function linkItemToCategories(
  payload: object
): RequestOptionsFactory<any> {
  function __linkItemToCategories({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.LinkItemToCategories',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/{itemId}/link-categories',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __linkItemToCategories;
}

/**
 * Unlinks an item from multiple categories. Removes specified category links while keeping others.
 *
 * To replace all category links, call [Overwrite Item Categories](#overwriteitemcategories). To add category links, call [Link Item To Categories](#linkitemtocategories).
 */
export function unlinkItemFromCategories(
  payload: object
): RequestOptionsFactory<any> {
  function __unlinkItemFromCategories({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.UnlinkItemFromCategories',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/{itemId}/unlink-categories',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __unlinkItemFromCategories;
}

/**
 * Replaces all existing category links with the specified categories.
 *
 * To add category links without removing existing ones, call [Link Item To Categories](#linkitemtocategories). To remove specific category links, call [Unlink Item From Categories](#unlinkitemfromcategories).
 */
export function overwriteItemCategories(
  payload: object
): RequestOptionsFactory<any> {
  function __overwriteItemCategories({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.OverwriteItemCategories',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseItemsServiceUrl({
        protoPath: '/v1/enterprise/items/{itemId}/overwrite-categories',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __overwriteItemCategories;
}
